import { useState, useEffect, useRef } from 'react';
import { addMinutes, subMinutes } from 'date-fns';
import { supabase as _supabase } from '@/integrations/supabase/client';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';
import type { CalendarEvent } from '@/hooks/useCalendarEvents';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const supabase = _supabase as any;

export type ReminderPhase = '5min' | 'exact';

export interface ReminderEvent {
  event: CalendarEvent;
  phase: ReminderPhase;
}

function lsKey(eventId: string, phase: ReminderPhase) {
  return `cal_${phase}_${eventId}`;
}

function markNotified(eventId: string, phase: ReminderPhase) {
  try {
    localStorage.setItem(lsKey(eventId, phase), '1');
  } catch {
    // ignore storage errors
  }
}

function wasNotified(eventId: string, phase: ReminderPhase) {
  try {
    return !!localStorage.getItem(lsKey(eventId, phase));
  } catch {
    return false;
  }
}

export function useCalendarMeetingReminder() {
  const { data: currentUser } = useCurrentAppUser();
  const [reminder, setReminder] = useState<ReminderEvent | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const check = async () => {
    if (!currentUser?.id) return;

    const now = new Date();
    const windowStart = subMinutes(now, 1).toISOString();
    const windowEnd = addMinutes(now, 6).toISOString();

    const { data, error } = await supabase
      .from('calendar_events')
      .select('*')
      .eq('type', 'meeting')
      .eq('status', 'scheduled')
      .gte('start_at', windowStart)
      .lte('start_at', windowEnd);

    if (error || !data) return;

    const userId = currentUser.id;

    // Filter to events the current user is involved in
    const mine: CalendarEvent[] = (data as CalendarEvent[]).filter(
      (ev) => ev.created_by === userId || (ev.attendee_ids ?? []).includes(userId),
    );

    // Determine phase for each event and pick first un-notified
    for (const ev of mine) {
      const start = new Date(ev.start_at);
      const diffMin = (start.getTime() - now.getTime()) / 60000;

      // Exact: -1min to +1min
      if (diffMin >= -1 && diffMin <= 1) {
        if (!wasNotified(ev.id, 'exact')) {
          setReminder({ event: ev, phase: 'exact' });
          return;
        }
      }

      // 5min: +4min to +6min
      if (diffMin >= 4 && diffMin <= 6) {
        if (!wasNotified(ev.id, '5min')) {
          setReminder({ event: ev, phase: '5min' });
          return;
        }
      }
    }
  };

  useEffect(() => {
    if (!currentUser?.id) return;

    check();
    intervalRef.current = setInterval(check, 60_000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  const dismiss = () => {
    if (!reminder) return;
    markNotified(reminder.event.id, reminder.phase);
    setReminder(null);
  };

  return { reminder, dismiss };
}
